// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The cars: integer kinematics, collective control, and the two rules that make the lift network a
// mechanic instead of a delivery service.
//
// ── EVERYTHING IS AN INTEGER ────────────────────────────────────────────────────────────────────
// Position is `posQ`, in thousandths of a storey; velocity is `velQ`, in thousandths of a storey per
// tick. Acceleration is a constant added to `velQ`. There is no float anywhere in the motion, so two
// runs of the same seed are byte-identical and the overtake audit compares integers rather than
// hunting for an epsilon that works.
//
// ── RULE ONE: A CAR NEVER PASSES A STOP IT HAS COMMITTED TO ─────────────────────────────────────
// `stops` is the set of storeys this car WILL open its doors at before it reverses. A storey only
// enters it if the car can still physically stop there — the dispatcher refuses to assign a landing
// call to a car that is already inside its own braking distance, and hands it to another car or to
// the next sweep instead. That refusal is what turns "did the car stop when it should have" into a
// question with no physics in it: the audit just checks that no `pass` event ever names a storey the
// car had promised, in the direction it promised it, with room to spare. A dispatcher that promises
// what it cannot deliver is the bug, and it is the bug this catches.
//
// ── RULE TWO: CARS IN ONE RISER CANNOT PASS EACH OTHER ──────────────────────────────────────────
// Two cars in a shaft are on the same rails, and `travelBounds` makes that true rather than assumed:
// no car may commit to a run that would take it past another car's position or committed target.
//
// A game shipping today allows exactly one car per riser (see `MAX_CARS_PER_RISER` in sim.ts, and the
// measurement that put it there), so this rule currently constrains nothing. It is kept because the
// constraint is a fact about lifts rather than a balance choice: the day a second car is allowed back
// into a shaft, the physics must already be honest, and the mechanism audit already checks that two
// cars never swap order. A rule that is true and inert is much cheaper than a rule that has to be
// re-derived under pressure.

import {
  CAR_EXPRESS,
  CAR_LOCAL,
  DOOR_BASE_TICKS,
  DOOR_MAX_TICKS,
  DOOR_MIN_TICKS,
  DOOR_PER_PERSON_TICKS,
  STOREYS,
  type CarSpec,
} from './tuning';
import type { Dir } from './events';

/** Thousandths of a storey. */
export const Q = 1000;

export interface Car {
  id: number;
  riser: number;
  express: boolean;
  cap: number;
  /** Position in thousandths of a storey. */
  posQ: number;
  /** Velocity in thousandths of a storey per tick, always non-negative; `dir` carries the sign. */
  velQ: number;
  vmaxQ: number;
  accelQ: number;
  dir: Dir;
  state: 'idle' | 'run' | 'doors';
  /** The storey this run ends at, or -1 when idle. */
  target: number;
  /**
   * Storeys this car has promised to open at, split by the direction it will be travelling when it
   * gets there. The split is what keeps collective control honest: a car going up must not stop for
   * somebody who wants to go down, and a rider's destination always belongs to the direction they
   * boarded in, so a car call needs no direction of its own.
   */
  stops: { up: Set<number>; down: Set<number> };
  riders: number[];
  doorUntil: number;
  idleSince: number;
  /** How many boarded at the stop currently standing open, for the `depart` event. */
  pendingBoarded: number;
  /** The stop another car is currently denying this one, so the event fires once and not every tick. */
  blockedOn: number;
  /** The last tick this car opened its doors. Drives the recall watchdog in sim.ts. */
  lastDoor: number;
  /** Extra door ticks for an express car — faster per storey, worse per stop. */
  doorExtra: number;
}

export const specOf = (express: boolean): CarSpec => (express ? CAR_EXPRESS : CAR_LOCAL);

export function createCar(id: number, riser: number, express: boolean, atStorey: number): Car {
  const s = specOf(express);
  return {
    id,
    riser,
    express,
    cap: s.cap,
    posQ: atStorey * Q,
    velQ: 0,
    vmaxQ: Math.round(s.vmax * 100),
    accelQ: Math.round(s.accel * 10),
    dir: 1,
    state: 'idle',
    target: -1,
    stops: { up: new Set(), down: new Set() },
    riders: [],
    doorUntil: 0,
    idleSince: 0,
    pendingBoarded: 0,
    blockedOn: -1,
    lastDoor: 0,
    doorExtra: s.doorExtra,
  };
}

/** The storey a car is at or nearest to, for anything that needs a whole number. */
export const carStorey = (c: Car): number => Math.round(c.posQ / Q);
/** Position as a float, for the renderer only. */
export const carPos = (c: Car): number => c.posQ / Q;

/** How far this car needs to come to a stop from its current speed, in thousandths of a storey. */
export const brakingQ = (c: Car): number => (c.velQ * c.velQ) / (2 * c.accelQ);

/**
 * Whether the car could still open its doors at `storey` while travelling `dir`. This is the function
 * the dispatcher consults before it makes a promise, and the reason the audit needs no physics.
 */
export function canStopAt(c: Car, storey: number, dir: Dir): boolean {
  const targetQ = storey * Q;
  const ahead = (targetQ - c.posQ) * dir;
  if (ahead < 0) return false;
  return ahead >= brakingQ(c);
}

/** Ticks the doors stand open for `n` people crossing the threshold. */
export const dwellTicks = (c: Car, n: number): number =>
  Math.min(DOOR_MAX_TICKS, Math.max(DOOR_MIN_TICKS, DOOR_BASE_TICKS + c.doorExtra + DOOR_PER_PERSON_TICKS * n));

/**
 * An estimate of how long a full-speed run of `d` storeys takes, in ticks. Used by the dispatcher to
 * rank cars and by route selection to compare risers; the real journey is integrated tick by tick, so
 * this is allowed to be an approximation — but it is the same closed-form trapezoid the integration
 * converges to, so it is a good one.
 */
export function runTicks(c: Car, d: number): number {
  if (d <= 0) return 0;
  const a = c.accelQ;
  const v = c.vmaxQ;
  const dQ = d * Q;
  const accelDist = (v * v) / (2 * a);
  if (dQ <= 2 * accelDist) return Math.ceil(2 * Math.sqrt(dQ / a));
  return Math.ceil((2 * v) / a + (dQ - 2 * accelDist) / v);
}

/**
 * The interval a car may commit a target inside, given the other cars sharing its riser. Bounded by
 * both their positions and their committed targets, because a bound taken from position alone lets
 * two cars pick crossing runs in the same tick.
 */
export function travelBounds(c: Car, siblings: readonly Car[]): { lo: number; hi: number } {
  let lo = 0;
  let hi = STOREYS - 1;
  for (const o of siblings) {
    if (o.id === c.id) continue;
    const oPos = o.posQ / Q;
    const oTarget = o.target >= 0 ? o.target : oPos;
    const oLow = Math.min(oPos, oTarget);
    const oHigh = Math.max(oPos, oTarget);
    // Order within a riser is fixed by id: a lower id always rides below a higher one.
    if (o.id < c.id) lo = Math.max(lo, Math.ceil(oHigh));
    else hi = Math.min(hi, Math.floor(oLow));
  }
  return { lo, hi };
}

/**
 * Advance a car one tick and report every storey boundary it crossed without stopping.
 *
 * Returns `arrived` when it has come to rest on its target this tick. `passed` is the storeys it went
 * by — the audit's single most important input, so it is produced by the motion itself rather than
 * being asserted somewhere by the code that decides where to stop.
 */
export function stepCar(c: Car, passed: number[]): boolean {
  passed.length = 0;
  if (c.state !== 'run' || c.target < 0) return false;

  const targetQ = c.target * Q;
  const before = c.posQ;
  const remaining = (targetQ - c.posQ) * c.dir;
  if (remaining <= brakingQ(c)) c.velQ = Math.max(c.accelQ, c.velQ - c.accelQ);
  else c.velQ = Math.min(c.vmaxQ, c.velQ + c.accelQ);

  c.posQ += c.velQ * c.dir;
  let arrived = false;
  if ((targetQ - c.posQ) * c.dir <= 0) {
    c.posQ = targetQ;
    c.velQ = 0;
    arrived = true;
  }

  // Every whole storey strictly between where it was and where it now is, excluding the one it just
  // stopped on, has been passed.
  const from = before / Q;
  const to = c.posQ / Q;
  // The bounds are STRICT at the `from` end. A car that starts a tick sitting exactly on storey 3 has
  // not passed storey 3 by leaving it, and `Math.ceil(3)` says otherwise — which would have every
  // departure log a phantom pass of its own landing, and the audit would then flag every single stop
  // the car had just made.
  if (c.dir === 1) {
    for (let s = Math.floor(from) + 1; s <= Math.floor(to); s++) if (!(arrived && s === c.target)) passed.push(s);
  } else {
    for (let s = Math.ceil(from) - 1; s >= Math.ceil(to); s--) if (!(arrived && s === c.target)) passed.push(s);
  }
  return arrived;
}
